/**
 * The published face of a post whose network leg is not confirmed yet.
 *
 * A network can accept a post and decide later (TikTok processes the media
 * after the vendor's 200). Until it gives a post id the post is "sent",
 * not live, and the card must say so instead of Published with no link
 * (TikTok Cooldown Trigger ticket, octakitty 4193, 2026-10-06). The state
 * itself comes from the backend (`leg_state`); this file only words it.
 */

export type LegState = 'confirmed' | 'pending' | 'unconfirmed' | 'failed';

export interface LegFace {
  label: string;
  sub?: string;
  /** The post may be viewed/deleted/marked deleted — only once confirmed. */
  postActions: boolean;
}

/** The face for a published row given its leg state. */
export function legFace(legState: LegState | null | undefined, networkName: string): LegFace {
  switch (legState) {
    case 'pending':
      return { label: 'Sent', sub: `awaiting ${networkName}`, postActions: false };
    case 'unconfirmed':
      return { label: 'Unconfirmed', sub: `check ${networkName}`, postActions: false };
    default:
      return { label: 'Published', postActions: true };
  }
}

/** A one-line note for the card's published view; null when confirmed. */
export function legNote(legState: LegState | null | undefined, networkName: string): string | null {
  switch (legState) {
    case 'pending':
      return `Sent · ${networkName} is still processing it. The link appears once it confirms.`;
    case 'unconfirmed':
      return `${networkName} never confirmed this post. Check the account before trusting it went out.`;
    default:
      return null;
  }
}

export const legAllowsPostActions = (legState: LegState | null | undefined): boolean =>
  legFace(legState, '').postActions;
